import { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BellAlertIcon, CheckBadgeIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { FiCheck, FiCopy } from "react-icons/fi";
import { ThemeContext } from "../components/ThemeContext";
import Post from "../services/Post";
import Reward from "../assets/images/reward.svg";
import { useBranding } from "../components/BrandingContext";
import "./Signup.css";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref")?.trim() || "";
  const { theme } = useContext(ThemeContext);
  const branding = useBranding();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", password_confirmation: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationHasToken, setRegistrationHasToken] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState("");
  const [idCopied, setIdCopied] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  useEffect(() => {
    document.title = "Create an account | TillionX";
  }, []);

  const register = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (Object.values(form).some((value) => !value.trim())) return setError("Please complete every field.");
    if (!acceptedTerms) return setError("Please accept the Terms of Use and Privacy Notice.");
    if (form.password !== form.password_confirmation) return setError("Passwords do not match.");
    try {
      setLoading(true);
      const response = await Post.Register({
        ...form,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      const payload = response?.data ?? response;
      const user = payload?.user ?? payload?.data ?? response?.user ?? (payload?.tillionx_id ? payload : null);
      const token = user?.token ?? payload?.token ?? response?.token;
      const tillionxId = user?.tillionx_id ?? payload?.tillionx_id ?? response?.tillionx_id ?? "";
      if (token) localStorage.setItem("token", token);
      if (user) {
        const storedUser = { ...user };
        delete storedUser.token;
        localStorage.setItem("user", JSON.stringify(storedUser));
      }
      setRegisteredUserId(tillionxId);
      setRegistrationHasToken(Boolean(token));
      setRegistrationComplete(true);
      setSuccess(tillionxId ? "Account created. Save this ID to receive TillionX transfers." : "Account created successfully.");
    } catch (requestError) {
      const message = requestError.message || "Unable to create the account.";
      if (/already exists|already been taken|already registered|account exists/i.test(message)) setExistingAccount(true);
      else setError(message);
    } finally {
      setLoading(false);
    }
  };

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(registeredUserId);
      setIdCopied(true);
    } catch {
      setError("Clipboard access is unavailable. Select and copy your TillionX ID.");
    }
  };

  return (
    <main className={`signup-page ${theme === "light" ? "signup-light" : ""}`}>
      <div className="signup-layout">
        <section className="signup-promo" aria-label="TillionX account benefits">
          <div className="signup-promo-copy">
            <p className="signup-eyebrow">WELCOME TO TILLIONX</p>
            <h1>Your next move<br /><span>starts here.</span></h1>
            <p className="signup-promo-description">Create an account to explore digital assets, trading tools, and rewards.</p>
          </div>
          <img className="signup-promo-art" src={Reward} alt="Digital asset rewards illustration" />
          <div className="signup-benefits">
            <div><TrophyIcon /><span>Tools for every market move</span></div>
            <div><CheckBadgeIcon /><span>Built for your next opportunity</span></div>
          </div>
        </section>

        <section className="signup-form-side" aria-label="Create your account">
          <div className="signup-card">
            <Link className="signup-card-brand" to="/" aria-label="TillionX home"><img src={branding.logo} alt="TillionX" /></Link>
            <h2>{registrationComplete ? "You're on your way" : "Welcome to TillionX"}</h2>
            {registrationComplete ? (
              <div className="signup-success">
                <p role="status" className="signup-success-message">{success}</p>
                {registeredUserId ? <div className="signup-id-panel">
                  <span>Your TillionX ID</span>
                  <div><strong>{registeredUserId}</strong><button type="button" title="Copy TillionX ID" aria-label={idCopied ? "TillionX ID copied" : "Copy TillionX ID"} onClick={copyUserId}>{idCopied ? <FiCheck /> : <FiCopy />}</button></div>
                  <p>Share this ID with people who want to transfer funds to you.</p>
                </div> : <p className="signup-error">Your account was created, but the API did not return a TillionX ID.</p>}
                {error && <p role="alert" className="signup-error">{error}</p>}
                <Link to={registrationHasToken ? "/home" : "/login"} className="signup-submit">Continue</Link>
              </div>
            ) : (
              <>
                <p className="signup-intro">Create an account</p>
                <form className="signup-form" onSubmit={register}>
                  <label htmlFor="signup-name">Full name</label>
                  <input id="signup-name" autoComplete="name" placeholder="Full name" value={form.name} onChange={update("name")} />
                  <label htmlFor="signup-email">Email</label>
                  <input id="signup-email" type="email" autoComplete="email" placeholder="Email address" value={form.email} onChange={update("email")} />
                  <label htmlFor="signup-phone">Phone number</label>
                  <input id="signup-phone" type="tel" autoComplete="tel" placeholder="Phone number" value={form.phone} onChange={update("phone")} />
                  <label htmlFor="signup-password">Password</label>
                  <input id="signup-password" type="password" autoComplete="new-password" placeholder="Create a password" value={form.password} onChange={update("password")} />
                  <label htmlFor="signup-confirm-password">Confirm password</label>
                  <input id="signup-confirm-password" type="password" autoComplete="new-password" placeholder="Confirm your password" value={form.password_confirmation} onChange={update("password_confirmation")} />
                  <label className="signup-consent"><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} /><span>By creating an account, I agree to TillionX's Terms of Use and Privacy Notice.</span></label>
                  {error && <p role="alert" className="signup-error">{error}</p>}
                  <button className="signup-submit" type="submit" disabled={loading}>{loading ? "Creating account..." : "Register"}</button>
                </form>
                <div className="signup-divider"><span>or</span></div>
                <div className="signup-links"><Link to="/entity">Sign up as an entity</Link><span>or</span><Link to="/login">Log in</Link></div>
              </>
            )}
          </div>
          <footer className="signup-footer"><span>© 2026 TillionX</span></footer>
        </section>
      </div>
      {existingAccount && <div className="signup-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setExistingAccount(false); }}>
        <section className="signup-modal" role="dialog" aria-modal="true" aria-labelledby="existing-account-title">
          <BellAlertIcon aria-hidden="true" />
          <h2 id="existing-account-title">Existing account</h2>
          <p>This account already exists. Please log in to continue.</p>
          <Link className="signup-submit" to="/login">Log in</Link>
          <button className="signup-cancel" type="button" onClick={() => setExistingAccount(false)}>Cancel</button>
        </section>
      </div>}
    </main>
  );
};

export default Signup;